import React, { useEffect, useState } from "react";

import { GTagEvents } from "../utils/index.js";
import { bf0 } from "../components/Chat/Quiz/Questions/bf0.jsx";
import { bf1 } from "../components/Chat/Quiz/Questions/bf1.jsx";
import { bf2 } from "../components/Chat/Quiz/Questions/bf2.jsx";
import { bf3 } from "../components/Chat/Quiz/Questions/bf3.jsx";
import { bf5 } from "../components/Chat/Quiz/Questions/bf5.jsx";
import { bf6 } from "../components/Chat/Quiz/Questions/bf6.jsx";

import {
  handleUpdateSession,
  updateIsFirstMessageSent,
  handleUpdateUser,
} from "./track.js";

const GTAG_IDS = {
  "liljathelabel.com": "G-TRBLT2RFSW",
  "lemonadedolls.com": "G-VGFXRL87HN",
  "www.monseraintimates.com": "G-ZY2ZRGX9JP",
  "f61ab3-d5.myshopify.com": "G-KVLJTJMMCJ",
};

export const useGTagScript = ({ theme }) => {
  const [fireEvent, setFireEvent] = useState(() => () => {});

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!theme) {
      return;
    }

    const GTAG_ID = theme.brarista_gtag_id;
    const tenantGTAGID = theme.tenant_gtag_id;

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.id = "my-iframe-id";

    iframe.onload = () => {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

      // code to listen for messages into the iframe
      iframeDoc.open();
      iframeDoc.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Iframe</title>
      </head>
      <body>
        <h1>Iframe Content</h1>
        <script>
          window.addEventListener('message', (event) => {
            if (!event?.data) {
                return;
            }

            const { eventName, payload } = event.data;

            if ('gtag' in window) {
                window.gtag("event", eventName, payload);
            }
          });
        </script>
      </body>
      </html>
    `);
      iframeDoc.close();
    };

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    const script1 = iframeDoc.createElement("script");
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
    script1.async = true;
    iframeDoc.head.appendChild(script1);

    // inline script for initializing GTag
    const inlineScript = iframeDoc.createElement("script");
    inlineScript.innerHTML =
      `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());` +
      (GTAG_ID
        ? `
      //  brarista google tag id
        gtag('config', '${GTAG_ID}');
      `
        : "") +
      (tenantGTAGID
        ? `
      //  client google tag id
        gtag('config', '${tenantGTAGID}');
    `
        : "");

    iframeDoc.head.appendChild(inlineScript);

    function fireEventNew(eventName, payload) {
      const host = window.location.host;
      const { sessionId, didSessionUpdate, isFirstMessageSent } =
        handleUpdateSession();
      const { userId, didUserUpdate } = handleUpdateUser();

      const finalPayload = { ...payload, host, sessionId, userId };

      const newSessionPayload = { host, sessionId, userId };

      if (didSessionUpdate) {
        iframe.contentWindow.postMessage(
          { eventName: GTagEvents.NewSession, payload: newSessionPayload },
          "*"
        );
      }

      const newConversationPayload = {
        sessionId,
        userId,
        path: payload?.path,
        host,
      };

      if (eventName === GTagEvents.SendMessage && !isFirstMessageSent) {
        iframe.contentWindow.postMessage(
          {
            eventName: GTagEvents.StartConversation,
            payload: newConversationPayload,
          },
          "*"
        );
        updateIsFirstMessageSent(true);
      }

      iframe.contentWindow.postMessage(
        { eventName, payload: finalPayload },
        "*"
      );

      if (host === "lemonadedolls.com" && "TriplePixel" in window) {
        TriplePixel("custom", eventName, finalPayload);

        if (didSessionUpdate) {
          TriplePixel("custom", GTagEvents.NewSession, newSessionPayload);
        }

        if (eventName === GTagEvents.SendMessage && !isFirstMessageSent) {
          TriplePixel(
            "custom",
            GTagEvents.StartConversation,
            newConversationPayload
          );
          // following isn't strictly necessary but doesn't hurt either
          updateIsFirstMessageSent(true);
        }
      }
    }

    setFireEvent(() => {
      return fireEventNew;
    });
    return () => {
      document.body.removeChild(iframe);
    };
  }, [theme]);

  return fireEvent;
};

//  update gifs for quizzes
export function useUpdateImages({ theme }) {
  useEffect(() => {
    if (!theme) {
      return;
    }

    const {
      quiz_bf0_cut_options,
      quiz_bf0_band_pull_options,
      quiz_bf0_loosest_hook,
      quiz_bf0_tightest_hook,
      quiz_bf0_scooped_breast,
      quiz_bf1_band_pull_options,
      quiz_bf2_cut_options,
      quiz_bf2_moulded_or_fabric,
      quiz_bf3_cut_options,
      quiz_bf5_shoulder_options,
      quiz_bf6_band_pull_options,
      quiz_bf6_tightest_hook,
    } = theme;

    if (quiz_bf0_cut_options) {
      const ques = bf0.find((question) => {
        return question.id === 6;
      });

      ques?.options.forEach((option, index) => {
        if (quiz_bf0_cut_options[index]?.url) {
          option.image = quiz_bf0_cut_options[index].url;
        }
      });
    }

    if (quiz_bf0_band_pull_options) {
      const ques = bf0.find((question) => {
        return question.id === 4;
      });

      ques?.options.forEach((option, index) => {
        if (quiz_bf0_band_pull_options[index]?.url) {
          option.image = quiz_bf0_band_pull_options[index].url;
        }
      });
    }

    if (quiz_bf0_loosest_hook) {
      const ques = bf0.find((question) => {
        return question.id === 9;
      });

      if (quiz_bf0_loosest_hook?.[0]?.url) {
        ques.image = quiz_bf0_loosest_hook?.[0]?.url;
      }
    }

    if (quiz_bf0_tightest_hook) {
      const ques = bf0.find((question) => {
        return question.id === 3;
      });

      if (quiz_bf0_tightest_hook?.[0]?.url) {
        ques.image = quiz_bf0_tightest_hook?.[0]?.url;
      }
    }

    if (quiz_bf0_scooped_breast) {
      const ques = bf0.find((question) => {
        return question.id === 5;
      });

      if (quiz_bf0_scooped_breast?.[0]?.url) {
        ques.image = quiz_bf0_scooped_breast?.[0]?.url;
      }
    }

    if (quiz_bf1_band_pull_options) {
      const ques = bf1.find((question) => {
        return question.id === 4;
      });

      ques?.options.forEach((option, index) => {
        if (quiz_bf1_band_pull_options[index]?.url) {
          option.image = quiz_bf1_band_pull_options[index].url;
        }
      });
    }

    if (quiz_bf2_cut_options) {
      const ques1 = bf2.find((question) => {
        return question.id === 4;
      });

      ques1?.options.forEach((option, index) => {
        if (quiz_bf2_cut_options[index]?.url) {
          option.image = quiz_bf2_cut_options[index].url;
        }
      });

      const ques2 = bf2.find((question) => {
        return question.id === 11;
      });

      ques2?.options.forEach((option, index) => {
        if (quiz_bf2_cut_options[index]?.url) {
          option.image = quiz_bf2_cut_options[index].url;
        }
      });
    }

    if (quiz_bf2_moulded_or_fabric) {
      const ques = bf2.find((question) => {
        return question.id === 3;
      });

      ques?.options.forEach((option, index) => {
        if (quiz_bf2_moulded_or_fabric[index]?.url) {
          option.image = quiz_bf2_moulded_or_fabric[index].url;
        }
      });
    }

    if (quiz_bf3_cut_options) {
      const ques = bf3.find((question) => {
        return question.id === 3;
      });

      ques?.options.forEach((option, index) => {
        if (quiz_bf3_cut_options[index]?.url) {
          option.image = quiz_bf3_cut_options[index].url;
        }
      });
    }

    if (quiz_bf5_shoulder_options) {
      const ques = bf5.find((question) => {
        return question.id === 2;
      });

      ques?.options.forEach((option, index) => {
        if (quiz_bf5_shoulder_options[index]?.url) {
          option.image = quiz_bf5_shoulder_options[index].url;
        }
      });
    }

    if (quiz_bf6_band_pull_options) {
      const ques = bf6.find((question) => {
        return question.id === 4;
      });

      ques?.options.forEach((option, index) => {
        if (quiz_bf6_band_pull_options[index]?.url) {
          option.image = quiz_bf6_band_pull_options[index].url;
        }
      });
    }

    if (quiz_bf6_tightest_hook) {
      const ques = bf6.find((question) => {
        return question.id === 3;
      });

      if (quiz_bf6_tightest_hook?.[0]?.url) {
        ques.image = quiz_bf6_tightest_hook?.[0]?.url;
      }
    }
  }, [theme]);
}
